<script lang="ts">
  import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
  import { 
    Server, 
    CheckCircle2, 
    Gauge, 
    GitBranch,
    Database,
    FileText,
    Award,
    Lock,
    AlertTriangle,
    Container,
    Code2,
    Search
  } from '@lucide/svelte';
	import { createPageSEO, createWebPageSchema, createBreadcrumbSchema, createFAQSchema } from '$lib/config/seo';

  const faqs = [
    {
      question: "How do you analyze DevOps repositories to assess experience?",
      answer: "We use static analysis tools combined with LLM-powered code review to examine infrastructure-as-code quality, CI/CD pipeline configurations, security practices, and architectural patterns. We look at metrics like secrets management, resource organization, error handling, documentation quality, and how code evolved over time. This gives us technical signals about their work, though interviews are still essential to understand context and decision-making."
    },
    {
      question: "Can you really tell junior vs senior DevOps engineers from their code?",
      answer: "We can identify many technical indicators: seniors typically show better infrastructure organization, proper secrets management, comprehensive error handling, documentation, and code that evolved thoughtfully over time. However, code analysis alone can't capture collaboration skills, incident response under pressure, or architectural decision-making. We provide technical signals that make your interviews more focused and efficient."
    },
    {
      question: "What specific things do you check in infrastructure code?",
      answer: "We analyze Terraform/CloudFormation for module organization and state management, check for hardcoded secrets or credentials, review CI/CD configs for testing and deployment strategies, examine Docker/K8s for resource limits and health checks, verify monitoring implementations, and assess documentation quality. We also look at commit patterns - how code evolved, how bugs were fixed, and whether best practices improved over time."
    },
    {
      question: "How do you handle private repositories or team contributions?",
      answer: "We primarily analyze public repositories and personal projects. For team repositories, we note that attribution is complex - we can see commit authorship but can't always determine who designed vs implemented. We're transparent about these limitations and focus on patterns across multiple repositories to get a fuller picture of someone's work."
    },
    {
      question: "Does this replace technical interviews?",
      answer: "No. Repository analysis is pre-interview screening that saves you time by filtering out obvious mismatches and highlighting promising candidates. You still need interviews to assess problem-solving, communication, incident response thinking, and architectural decision-making. We help you spend interview time on candidates who've demonstrated technical competency in their actual work."
    },
    {
      question: "What if someone's best work is in private company repos?",
      answer: "That's a common limitation. We encourage candidates to include portfolio projects, open-source contributions, or sanitized examples of their work. Our analysis works best for candidates with public technical work. For those without, traditional screening methods are still necessary."
    }
  ];

  const seo = createPageSEO({
    title: 'Hire DevOps Engineers - GitHub Repository Analysis | TalentProfile',
    description: 'Find DevOps engineers by analyzing their actual infrastructure code, CI/CD configs, and deployment patterns. Technical screening before interviews. Free.',
    canonical: '/hire-devops-engineers',
    keywords: 'hire DevOps engineers, GitHub screening, infrastructure code analysis, Kubernetes engineer hiring, AWS DevOps jobs, technical screening',
    structuredData: [
      createWebPageSchema({
        name: 'Hire DevOps Engineers',
        description: 'Recruit DevOps engineers using automated analysis of their infrastructure code and deployment configurations',
        url: '/hire-devops-engineers'
      }),
      createBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Hire', url: '/hire' },
        { name: 'DevOps Engineers' }
      ]),
      createFAQSchema(faqs)
    ]
  });

  const stats = [
    { value: "70%", label: "Time Saved", description: "On initial technical screening before interviews" },
    { value: "85%", label: "Accuracy", description: "In identifying technical skill mismatches" },
    { value: "50+", label: "Code Patterns", description: "Analyzed per repository automatically" },
    { value: "2.8x", label: "Better Matches", description: "Compared to resume keyword screening" }
  ];

  const whatWeAnalyze = [
    {
      icon: Code2,
      title: "Infrastructure-as-Code Quality",
      description: "Static analysis of Terraform, CloudFormation, and Ansible",
      checks: [
        "Module organization and reusability patterns",
        "State management and remote backend configurations",
        "Variable and output declarations",
        "Resource naming conventions and tagging",
        "Documentation and README completeness"
      ]
    },
    {
      icon: Lock,
      title: "Security Practices",
      description: "Automated scanning for security anti-patterns",
      checks: [
        "Hardcoded secrets, API keys, or credentials in code or history",
        "IAM policy structure and principle of least privilege",
        "Security group and firewall rule configurations",
        "Secrets management tool usage (Vault, AWS Secrets Manager)",
        "Container image scanning configurations"
      ]
    },
    {
      icon: GitBranch,
      title: "CI/CD Pipeline Configuration",
      description: "Review of automation and deployment strategies",
      checks: [
        "Pipeline structure and job organization",
        "Testing stages and quality gates",
        "Deployment strategies (blue-green, canary indicators)",
        "Error handling and rollback mechanisms",
        "Environment-specific configurations"
      ]
    },
    {
      icon: Container,
      title: "Container & Orchestration",
      description: "Docker and Kubernetes manifest analysis",
      checks: [
        "Dockerfile multi-stage builds and layer optimization",
        "Resource limits and requests in K8s manifests",
        "Health check and readiness probe configurations",
        "ConfigMap and Secret usage patterns",
        "Service mesh and ingress configurations"
      ]
    },
    {
      icon: Gauge,
      title: "Monitoring & Observability",
      description: "Implementation of logging and metrics",
      checks: [
        "Prometheus, Grafana config presence and structure",
        "Custom metrics and dashboard definitions",
        "Log aggregation configurations",
        "Alert rule definitions and thresholds",
        "Distributed tracing setup"
      ]
    },
    {
      icon: Database,
      title: "Code Evolution & Patterns",
      description: "Commit history and development patterns",
      checks: [
        "Commit message quality and consistency",
        "How infrastructure evolved over time",
        "Bug fix patterns and problem-solving approach",
        "Documentation updates alongside code changes",
        "Response to issues and pull request activity"
      ]
    }
  ];

  const technicalIndicators = [
    {
      category: "Beginner Indicators",
      color: "red",
      signs: [
        "Monolithic Terraform files with no module organization",
        "Hardcoded values throughout infrastructure code",
        "No variable validation or type constraints",
        "Missing or minimal documentation",
        "Credentials or secrets checked into repository history",
        "No environment separation (dev/staging/prod)",
        "Copy-pasted code blocks with slight modifications",
        "Kubernetes deployments without resource limits",
        "CI/CD pipelines with only basic deploy steps"
      ]
    },
    {
      category: "Intermediate Indicators",
      color: "yellow",
      signs: [
        "Basic module structure in Terraform/CloudFormation",
        "Environment variables used for configuration",
        "Some documentation present but inconsistent",
        "Basic CI/CD with testing stages",
        "Secrets management partially implemented",
        "Resource tagging for some infrastructure",
        "Health checks defined in container orchestration",
        "Monitoring configured but basic dashboards",
        "Commit messages are functional but not detailed"
      ]
    },
    {
      category: "Senior Indicators",
      color: "green",
      signs: [
        "Well-organized, reusable infrastructure modules",
        "Comprehensive variable validation and type safety",
        "Detailed documentation with architecture decisions",
        "Sophisticated CI/CD with matrix testing and rollback",
        "Consistent secrets management across all services",
        "Complete resource tagging strategy for cost allocation",
        "Comprehensive monitoring with custom metrics and SLOs",
        "Evidence of refactoring and code quality improvements",
        "Thoughtful commit messages explaining 'why' not just 'what'"
      ]
    }
  ];

  const realWorldExample = {
    scenario: "Analyzing a Kubernetes Configuration",
    junior: `apiVersion: apps/v1
kind: Deployment
metadata:
  name: myapp
spec:
  replicas: 1
  template:
    spec:
      containers:
      - name: app
        image: myapp:latest
        env:
        - name: DB_PASSWORD
          value: "supersecret123"`,
    juniorIssues: [
      "Hardcoded secret in plain text",
      "Using 'latest' tag (non-deterministic)",
      "No resource limits or requests",
      "No health checks or probes",
      "Fixed replica count with no autoscaling"
    ],
    senior: `apiVersion: apps/v1
kind: Deployment
metadata:
  name: myapp
  labels:
    app: myapp
    env: production
spec:
  replicas: 3
  strategy:
    rollingUpdate:
      maxSurge: 1
      maxUnavailable: 0
  template:
    spec:
      containers:
      - name: app
        image: myapp:v1.2.3
        resources:
          requests:
            memory: "128Mi"
            cpu: "100m"
          limits:
            memory: "256Mi"
            cpu: "200m"
        env:
        - name: DB_PASSWORD
          valueFrom:
            secretKeyRef:
              name: db-credentials
              key: password
        livenessProbe:
          httpGet:
            path: /health
            port: 8080
        readinessProbe:
          httpGet:
            path: /ready
            port: 8080`,
    seniorStrengths: [
      "Secrets properly referenced from K8s Secret",
      "Specific version tag for reproducibility",
      "Resource limits prevent resource exhaustion",
      "Health checks enable automatic recovery",
      "Rolling update strategy for zero downtime",
      "Proper labeling for organization"
    ]
  };

  const analysisLimitations = [
    {
      limitation: "Can't Assess Collaboration & Communication",
      reality: "Repository analysis shows individual technical work but can't evaluate how someone works with teams, handles stakeholder communication, or navigates organizational complexity. These soft skills are critical for senior DevOps roles."
    },
    {
      limitation: "Team vs Individual Contributions",
      reality: "In company repositories, it's difficult to distinguish who designed the architecture vs who implemented it, or who mentored whom. Commit authorship doesn't tell the full story of team dynamics and contributions."
    },
    {
      limitation: "Context Behind Decisions",
      reality: "Code doesn't explain why certain trade-offs were made. A 'simple' solution might have been the right choice given organizational constraints, timeline pressures, or technical debt priorities that aren't visible in the repository."
    },
    {
      limitation: "Private Repository Limitations",
      reality: "Many engineers' best work is in private company repositories. Public repos and side projects give signals about technical ability, but may not represent someone's full professional experience or capabilities."
    },
    {
      limitation: "Incident Response & Problem-Solving",
      reality: "How someone handles a production outage at 2 AM involves troubleshooting skills, stress management, and decision-making under pressure - none of which are visible in repository code. Post-incident commits show outcomes, not the problem-solving process."
    }
  ];

  const howItHelps = [
    {
      icon: Search,
      title: "Pre-Interview Technical Screening",
      description: "Automatically filter candidates by actual technical work before spending interview time. Identify obvious mismatches and promising candidates worth deeper evaluation.",
      value: "Save 60-70% of initial screening time"
    },
    {
      icon: CheckCircle2,
      title: "Technology Stack Verification",
      description: "Confirm candidates have actually worked with the tools they claim on their resume - not just listed keywords but real, working code with those technologies.",
      value: "Eliminate resume padding and false claims"
    },
    {
      icon: Code2,
      title: "Code Quality Signals",
      description: "Get objective technical indicators about infrastructure organization, security awareness, and engineering maturity to inform interview questions and assess seniority level.",
      value: "Focus interviews on the right topics"
    },
    {
      icon: Award,
      title: "Find Hidden Talent",
      description: "Discover strong engineers who might be overlooked by traditional recruiters - people with great code but less polished resumes or non-traditional backgrounds.",
      value: "Access undervalued talent pools"
    }
  ];

  const comparisonPoints = [
    {
      traditional: "Scan resumes for DevOps keywords and certifications",
      talentProfile: "Analyze actual infrastructure code for technical patterns and quality"
    },
    {
      traditional: "Phone screen to verify basic tool knowledge",
      talentProfile: "See proof of hands-on experience with specific tools in their repositories"
    },
    {
      traditional: "Hope their AWS certification means production experience",
      talentProfile: "Review real Terraform/CloudFormation showing what they've actually built"
    },
    {
      traditional: "Give take-home assignment (high dropout rate)",
      talentProfile: "Review existing work they've already completed and published"
    },
    {
      traditional: "Spend interview time on basic technical screening",
      talentProfile: "Start interviews with candidates who've demonstrated baseline competency"
    },
    {
      traditional: "Discover skill mismatches after hiring",
      talentProfile: "Identify technical gaps before extending offers"
    }
  ];

  const techStackExamples = [
    {
      category: "Cloud Platforms",
      technologies: "AWS, Google Cloud Platform, Microsoft Azure, DigitalOcean"
    },
    {
      category: "Infrastructure as Code",
      technologies: "Terraform, CloudFormation, Pulumi, Ansible, Chef, Puppet"
    },
    {
      category: "Containers and Orchestration",
      technologies: "Kubernetes, Docker, Helm, ECS, EKS, GKE, AKS"
    },
    {
      category: "CI/CD Tools",
      technologies: "GitHub Actions, GitLab CI, Jenkins, CircleCI, ArgoCD, Flux"
    },
    {
      category: "Monitoring & Logging",
      technologies: "Prometheus, Grafana, ELK Stack, Datadog, New Relic, Splunk"
    },
    {
      category: "Service Mesh & Networking",
      technologies: "Istio, Linkerd, Nginx, Traefik, Envoy, HAProxy"
    }
  ];
</script>

{#key $page.url.pathname}
  <SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-white">
  <!-- Hero Section -->
  <section class="pt-20 pb-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Server class="w-4 h-4" />
          DevOps Engineer Recruitment
        </div>
        <h1 class="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Hire DevOps Engineers Using<br />
          <span class="text-blue-600">Automated Repository Analysis</span>
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Screen DevOps candidates by analyzing their actual infrastructure code, CI/CD configurations, and deployment patterns. See technical competency before interviews, not just resume keywords.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <a href="/drop-jd" class="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
            <FileText class="w-5 h-5" />
            Post Job Description
          </a>
          <a href="/how-it-works" class="bg-white text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors border-2 border-gray-200">
            See How It Works
          </a>
        </div>
        <p class="text-sm text-gray-600 max-w-2xl mx-auto">
          Repository analysis complements interviews - we provide technical signals to make your hiring more efficient, not replace human judgment.
        </p>
      </div>

      <!-- Stats -->
      <div class="grid md:grid-cols-4 gap-6 mb-16">
        {#each stats as stat (stat.value)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-lg transition-shadow">
            <div class="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
            <div class="text-sm font-semibold text-gray-900 mb-1">{stat.label}</div>
            <div class="text-xs text-gray-600">{stat.description}</div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- What We Analyze -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        What We Analyze in DevOps Repositories
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        We use static analysis tools combined with LLM-powered code review to examine infrastructure code quality, security practices, and engineering patterns. Here's what our automated analysis checks.
      </p>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {#each whatWeAnalyze as item (item.title)}
          {@const Icon = item.icon}
          <div class="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="p-3 bg-orange-100 rounded-full w-fit mb-4">
              <Icon class="w-6 h-6 text-orange-600" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
            <p class="text-gray-600 text-sm mb-4">{item.description}</p>
            <ul class="space-y-2">
              {#each item.checks as check (check)}
                <li class="flex items-start gap-2 text-sm text-gray-700">
                  <div class="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2 shrink-0"></div>
                  <span>{check}</span>
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Technical Indicators -->
  <section class="py-16 px-4">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Technical Seniority Indicators We Detect
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        Our analysis identifies patterns that correlate with different experience levels. These are technical signals, not definitive judgments - interviews provide the full picture.
      </p>
      <div class="space-y-6">
        {#each technicalIndicators as indicator (indicator.category)}
          {@const borderColor = indicator.color === 'green' ? 'border-green-200' : indicator.color === 'yellow' ? 'border-yellow-200' : 'border-red-200'}
          {@const bgColor = indicator.color === 'green' ? 'bg-green-50' : indicator.color === 'yellow' ? 'bg-yellow-50' : 'bg-red-50'}
          {@const textColor = indicator.color === 'green' ? 'text-green-700' : indicator.color === 'yellow' ? 'text-yellow-700' : 'text-red-700'}
          
          <div class="bg-white rounded-xl border-2 {borderColor} overflow-hidden">
            <div class="{bgColor} px-6 py-4 border-b {borderColor}">
              <h3 class="text-xl font-bold {textColor}">{indicator.category}</h3>
            </div>
            <div class="p-6">
              <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {#each indicator.signs as sign (sign)}
                  <div class="flex items-start gap-2">
                    <div class="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0"></div>
                    <span class="text-sm text-gray-700">{sign}</span>
                  </div>
                {/each}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Real World Example -->
  <section class="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Real Example: What Our Analysis Detects
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Here's a concrete example of how we differentiate technical skill levels through code analysis
      </p>
      
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4">
          <h3 class="text-xl font-bold text-white">{realWorldExample.scenario}</h3>
        </div>
        
        <div class="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          <div class="p-6">
            <div class="flex items-center gap-2 mb-4">
              <div class="px-3 py-1 bg-red-100 text-red-800 rounded-full">
                <span class="text-xs font-bold">BEGINNER CODE</span>
              </div>
            </div>
            <div class="bg-gray-900 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre class="text-green-400 text-xs font-mono">{realWorldExample.junior}</pre>
            </div>
            <div class="space-y-2">
              <p class="text-sm font-bold text-gray-900 mb-2">Issues Detected:</p>
              {#each realWorldExample.juniorIssues as issue (issue)}
                <div class="flex items-start gap-2">
                  <AlertTriangle class="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <span class="text-sm text-gray-700">{issue}</span>
                </div>
              {/each}
            </div>
          </div>
          
          <div class="p-6 bg-green-50/30">
            <div class="flex items-center gap-2 mb-4">
              <div class="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                <span class="text-xs font-bold">SENIOR CODE</span>
              </div>
            </div>
            <div class="bg-gray-900 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre class="text-green-400 text-xs font-mono">{realWorldExample.senior}</pre>
            </div>
            <div class="space-y-2">
              <p class="text-sm font-bold text-gray-900 mb-2">Strengths Identified:</p>
              {#each realWorldExample.seniorStrengths as strength (strength)}
                <div class="flex items-start gap-2">
                  <CheckCircle2 class="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <span class="text-sm text-gray-700">{strength}</span>
                </div>
              {/each}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- How It Helps -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        How Repository Analysis Improves Your Hiring
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        We're not replacing interviews - we're making your screening process more efficient and focused
      </p>
      <div class="grid md:grid-cols-2 gap-8">
        {#each howItHelps as item (item.title)}
          <div class="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="flex items-start gap-4 mb-4">
              <div class="p-3 bg-orange-100 rounded-lg shrink-0">
                <svelte:component this={item.icon} class="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p class="text-gray-700 leading-relaxed mb-4">{item.description}</p>
                <div class="bg-orange-50 px-4 py-2 rounded-lg inline-block">
                  <p class="text-sm font-semibold text-orange-700">{item.value}</p>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Analysis Limitations -->
  <section class="py-16 px-4 bg-blue-50">
    <div class="max-w-5xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        What Repository Analysis Can't Tell You
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        We're transparent about limitations. Here's what you still need interviews to assess.
      </p>
      <div class="space-y-4">
        {#each analysisLimitations as item (item.reality)}
          <div class="bg-white rounded-xl border border-blue-200 p-6">
            <h3 class="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle class="w-5 h-5 text-blue-600" />
              {item.limitation}
            </h3>
            <p class="text-gray-700 leading-relaxed pl-7">{item.reality}</p>
          </div>
        {/each}
      </div>
      <div class="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-xl">
        <p class="text-white text-center leading-relaxed">
          <strong class="font-bold">Our Philosophy:</strong> Repository analysis is a powerful screening tool, but hiring decisions require human judgment. We provide technical signals to make your interviews more efficient and focused, not to replace them.
        </p>
      </div>
    </div>
  </section>

  <!-- Tech Stack Coverage -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Technologies We Can Verify
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Our analysis covers the major DevOps tools and platforms - confirming candidates have actually used what they claim
      </p>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {#each techStackExamples as stack (stack.category)}
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-900 mb-3">{stack.category}</h3>
            <p class="text-gray-700 text-sm">{stack.technologies}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Comparison -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Traditional Screening vs Repository Analysis
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        See how automated code analysis improves on resume-based screening
      </p>
      <div class="space-y-6">
        {#each comparisonPoints as point, index (point.traditional)}
          <div class="bg-gradient-to-r from-white to-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div class="bg-gradient-to-r from-gray-100 to-gray-50 px-6 py-3 border-b border-gray-200">
              <span class="text-xs font-bold text-gray-500">SCREENING STEP #{index + 1}</span>
            </div>
            <div class="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              <div class="p-6">
                <div class="flex items-start gap-3">
                  <div class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-1">
                    <span class="text-sm text-red-600 font-bold">✕</span>
                  </div>
                  <div>
                    <p class="text-xs font-bold text-red-600 mb-2">TRADITIONAL</p>
                    <p class="text-gray-700 leading-relaxed">{point.traditional}</p>
                  </div>
                </div>
              </div>
              <div class="p-6 bg-green-50/30">
                <div class="flex items-start gap-3">
                  <div class="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center shrink-0 mt-1">
                    <CheckCircle2 class="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p class="text-xs font-bold text-green-700 mb-2">TALENTPROFILE</p>
                    <p class="text-gray-900 leading-relaxed font-medium">{point.talentProfile}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Frequently Asked Questions
      </h2>
      <div class="space-y-6">
        {#each faqs as faq (faq.question)}
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
            <p class="text-gray-700 leading-relaxed">{faq.answer}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-20 px-4 bg-gradient-to-r from-orange-600 to-red-600">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl font-bold text-white mb-6">
        Ready to Screen DevOps Engineers More Efficiently?
      </h2>
      <p class="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
        Drop your job description. Get candidates pre-screened by automated analysis of their infrastructure code. Save time on initial screening, focus interviews on qualified candidates. Free forever.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="/drop-jd" class="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors inline-flex items-center justify-center gap-2">
          <FileText class="w-5 h-5" />
          Post Job Description
        </a>
        <a href="/hire" class="bg-orange-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-400 transition-colors">
          Learn More About Hiring
        </a>
      </div>
      <p class="text-orange-200 mt-6 text-sm flex items-center justify-center gap-2">
        <Code2 class="w-4 h-4" /> Automated code analysis • Technical screening • Free
      </p>
    </div>
  </section>
</div>